/**
 * @deprecated Use the new system
 *
 * TODO: Move all this to @core/apis/shared as required
 */
import { useMemo } from "react";
import { useQueries, useQuery } from "@tanstack/react-query";
import { DateTime } from "luxon";

import { useDataApi } from "@core/apis";
import {
  getAverageSensorDataPerSubstationOptions,
  getSensorsDataPerSubstation,
  getSensorsPerSubstationOptions,
  SensorTypeTechnicalName,
  type Sensor,
} from "@core/apis/shared";

export const useClimateSensorsQuery = (substationUid?: string) => {
  const dataApi = useDataApi();

  return useQuery({
    ...getSensorsPerSubstationOptions(dataApi, substationUid),
    enabled: !!substationUid && !!dataApi,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    select(data) {
      const flattened = data?.flatMap((flexStation: any) => flexStation?.sensors ?? []) || [];
      return flattened.filter(
        (sensor: Sensor) => sensor.sensor_type_technical_name === "living_air_temperature"
      );
    },
  });
};

export const usePrimaryMeterSensorQuery = (substationUid?: string) => {
  const dataApi = useDataApi();
  return useQuery({
    ...getSensorsPerSubstationOptions(dataApi, substationUid),
    select(data) {
      return data
        ?.flatMap((flexStation: any) => flexStation?.sensors ?? [])
        .filter((sensor: any) => sensor.sensor_type_technical_name === "primary_meter");
    },
  });
};

export const useGetAverageSensorDataQuery = (
  substationUid: string,
  sensorType: SensorTypeTechnicalName,
  dateRange: { startDate: DateTime; endDate: DateTime },
  options?: { enabled?: boolean }
) => {
  const dataApi = useDataApi();
  return useQuery({
    ...getAverageSensorDataPerSubstationOptions(
      dataApi,
      substationUid,
      sensorType,
      dateRange.startDate,
      dateRange.endDate
    ),
    enabled: options?.enabled !== false && !!substationUid && !!sensorType,
    gcTime: 15 * 60 * 1000,
    refetchOnWindowFocus: false,
    select(data) {
      return data;
    },
  });
};

// Enhanced client-side batching configuration
const BATCH_SIZE = 5; // Increased from 3 to 5 for better throughput
const STAGGER_DELAY = 100; // Reduced from 200ms to 100ms for faster loading

export const useGetSensorsDataQuery = (
  substationUid: string,
  sensorUids: string[],
  dateRange: { startDate: DateTime; endDate: DateTime },
  options?: { enabled?: boolean }
) => {
  const dataApi = useDataApi();

  // Create staggered queries to prevent API flooding
  const staggeredQueries = useMemo(() => {
    return (sensorUids ?? []).map((sensorUid, index) => {
      // Stagger requests with incremental delays
      const staggerDelay = Math.floor(index / BATCH_SIZE) * STAGGER_DELAY;

      return {
        ...getSensorsDataPerSubstation(
          dataApi,
          substationUid,
          sensorUid,
          dateRange.startDate,
          dateRange.endDate
        ),
        enabled: options?.enabled !== false && !!sensorUid && !!substationUid,
        queryKey: [
          "sensor-data-throttled",
          substationUid,
          sensorUid,
          dateRange.startDate.toISO(),
          dateRange.endDate.toISO(),
          `stagger-${staggerDelay}`, // Include stagger in key for proper caching
        ],
        networkMode: "offlineFirst" as const,
        staleTime: 30 * 60 * 1000, // 30 minutes - very aggressive caching
        gcTime: 60 * 60 * 1000, // 60 minutes - keep data much longer
        refetchOnWindowFocus: false,
        refetchOnMount: false,
        refetchOnReconnect: false,
        refetchInterval: false, // Disable automatic refetching
        retry: 2,
        retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 5000),
        meta: {
          sensorUid,
          staggerDelay,
          batchNumber: Math.floor(index / BATCH_SIZE),
        },
      };
    });
  }, [
    dataApi,
    substationUid,
    sensorUids,
    dateRange.startDate,
    dateRange.endDate,
    options?.enabled,
  ]);

  return useQueries({
    queries: staggeredQueries,
    combine: (results) => {
      const sensorReadings: Record<string, any> = {};
      let hasData = false;
      let isLoadingAny = false;

      results.forEach((result, index) => {
        const sensorUid = sensorUids[index];
        if (result.isLoading) {
          isLoadingAny = true;
        }
        if (result.isSuccess && result.data) {
          sensorReadings[sensorUid] = result.data;
          hasData = true;
        } else if (result.isError) {
          console.warn(`Sensor data failed for ${sensorUid}:`, result.error);
          sensorReadings[sensorUid] = [];
        } else {
          sensorReadings[sensorUid] = [];
        }
      });

      return {
        data: hasData ? sensorReadings : undefined,
        isSuccess: results.some((result) => result.isSuccess), // Success if any succeed
        isLoading: isLoadingAny,
        isError: results.every((result) => result.isError), // Error only if all fail
      };
    },
  });
};
